import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  getSupportedDefaultFieldMappings,
  getSupportedMergeSourcePropertyMappings,
  MergeHRISIntegration
} from '@dadohr/shared/domainTypes/integrations/merge';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  UserImportDataSourceMergeHRIS,
  UserImportDataSourceStatus,
  UserImportMapping,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import {
  Button,
  DialogActions,
  DialogContent,
  Typography
} from '@material-ui/core';
import { Info } from 'lucide-react';
import { useMemo, useState } from 'react';
import shortid from 'shortid';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../../components/InfoBox';
import { Loader } from '../../../../../../components/Loader';
import { MailTo } from '../../../../../../components/MailTo';
import { SomethingWentWrong } from '../../../../../../components/SomethingWentWrong';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { setDoc } from '../../../../../../services/firebase';
import { now } from '../../../../../../services/time';
import { useUserFields } from '../../../../../../services/user';
import { useMergeConfig } from '../../../../../Org/pages/Integrations/integrations/merge/service';
import { MergeImportMappingForm } from '../CustomImportMappingForm';
import { Img, ImportSourceListItem } from './common';
import { MergeHRISIntegrationsInfoMap } from './meta';

const InfoContent = styled('div')((p) => ({}));
const InfoContainer = styled('div')((p) => ({
  display: 'flex',
  gap: p.theme.spacing(1)
}));

const LogoImg = styled(Img)((p) => ({
  marginBottom: p.theme.spacing(1),
  marginRight: p.theme.spacing(2)
}));

export const MergeInfoPanel = ({ name }: { name: string }) => {
  const { t } = useUnsafeTranslation();
  return (
    <InfoBox color="blue">
      <InfoContainer>
        <Info size={26} />

        <InfoContent>
          <strong>{name}</strong> {t(' is a ')} <strong>{t('Basic')}</strong>
          {t(
            ' integration. It can only read from limited fields and syncing new data can take up to one hour. To upgrade reach out to '
          )}
          <MailTo email="help@dadohr.com" color="primary">
            help@dadohr.com
          </MailTo>
        </InfoContent>
      </InfoContainer>
    </InfoBox>
  );
};

export const MergeHRISSetupFlow = ({
  orgId,
  onClose,
  customFields,
  mregeIntegration: mergeHRIS
}: {
  orgId: string;
  onClose: () => void;
  customFields: CustomUserField[];
  mregeIntegration: MergeHRISIntegration;
}) => {
  const { t } = useUnsafeTranslation();
  const { id: userId } = useCurrentUser();
  const userFields = useUserFields(customFields);
  const [config, loading, error] = useMergeConfig(orgId);
  const importSourceInfo = MergeHRISIntegrationsInfoMap[mergeHRIS];

  const { defaultMappings, supportedMergeFields } = useMemo(() => {
    const defaultMappings = getSupportedDefaultFieldMappings(mergeHRIS);
    const supportedMergeFields = getSupportedMergeSourcePropertyMappings(
      mergeHRIS
    );
    return { defaultMappings, supportedMergeFields };
  }, [mergeHRIS]);

  const [mappings, setMappings] = useState<UserImportMapping[]>(
    defaultMappings
  );

  if (loading) {
    return (
      <DialogContent>
        <Loader height={250} />
      </DialogContent>
    );
  }
  if (error) {
    return <SomethingWentWrong height={150} />;
  }
  if (!config) {
    return null;
  }

  return (
    <>
      {mappings && importSourceInfo && (
        <DialogContent>
          <FormRow>
            {/* <LogoContainer> */}
            <LogoImg maxHeight={32} src={importSourceInfo.logoUrl} />{' '}
            {/* </LogoContainer> */}
            <Typography variant="h2">{importSourceInfo.name}</Typography>
          </FormRow>
          <MergeInfoPanel name={importSourceInfo.name} />

          <MergeImportMappingForm
            additionalCompositeFields={['$FULL_NAME$']}
            userFields={userFields}
            integrationName={importSourceInfo.name}
            name={t('Default Field Mapping')}
            mappings={mappings}
            supportedMergeFields={supportedMergeFields}
            onChange={setMappings}
          />
        </DialogContent>
      )}
      {mappings && importSourceInfo && (
        <DialogActions>
          <Button onClick={onClose}>{t('Cancel')}</Button>
          <ButtonWithPromise
            color="primary"
            variant="contained"
            pending={`${t('Saving')}...`}
            onClick={async () => {
              const nextSource: Doc<UserImportDataSourceMergeHRIS> = {
                id: shortid(),
                collection: FS.userImportDataSources,
                data: {
                  orgId: orgId,
                  type: UserImportSourceType.MERGE_HRIS,
                  status: UserImportDataSourceStatus.PENDING,
                  detailedStatus: '',
                  lastUpdate: null,
                  createdBy: userId,
                  createdAt: now(),
                  customMappings: mappings.filter((m) => !!m.otherKey),
                  name: importSourceInfo?.name,
                  hris: mergeHRIS
                }
              };
              await setDoc(nextSource);
              onClose();
            }}
          >
            {t('Add source')}
          </ButtonWithPromise>
        </DialogActions>
      )}
    </>
  );
};

export const ImportSourceRowMergeHRIS = ({
  d
}: {
  d: Doc<UserImportDataSourceMergeHRIS>;
}) => {
  const info = MergeHRISIntegrationsInfoMap[d.data.hris];
  return (
    <ImportSourceListItem
      enabled={d.data.status === UserImportDataSourceStatus.OK}
      name={d.data.name || info?.name || 'Merge'}
      logoUrl={info?.smallLogoUrl || ''}
    />
  );
};
